import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import PajakReminderList from "@/components/app/PajakReminderList";
import TrendKeuanganChart from "@/components/app/TrendKeuanganChart";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, TrendingUp, TrendingDown, Landmark, Scale, Receipt } from "lucide-react";

const Stat = ({ label, value, color, icon: Icon, testid }) => (
  <div className="card-flat p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      {Icon && <Icon size={18} className={color} />}
    </div>
    <div className={`mt-3 font-mono text-2xl ${color}`}>{rupiah(value)}</div>
  </div>
);

export default function DashboardPribadi() {
  const nowY = new Date().getFullYear();
  const [tahun, setTahun] = useState(nowY);
  const [d, setD] = useState({ harta_total: 0, utang_total: 0, kekayaan_bersih: 0, total_saldo_akun: 0, akun: [], penghasilan_neto: 0, total_pph: 0, pph_kurang_bayar: 0, pph_lebih_bayar: 0 });

  const load = async () => { const r = await api.get("/dashboard/pribadi", { params: { tahun } }); setD((s) => ({ ...s, ...r.data })); };
  useEffect(() => { load(); }, [tahun]);

  return (
    <>
      <PageHeader title="Dashboard Keuangan Pribadi" subtitle="Fokus pada Kekayaan Bersih (Net Worth) & posisi pajak." actions={
        <div className="card-flat px-3 py-2 flex items-center gap-2"><Label className="text-xs">Tahun</Label><Input type="number" className="w-24 h-8" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-testid="dash-tahun" /></div>
      } />
      <div className="p-4 md:p-8 space-y-6">
        <PajakReminderList />
        <div className="card-flat p-8 bg-gradient-to-br from-blue-600 to-blue-800 text-white" data-testid="card-networth">
          <div className="flex items-center gap-2 text-blue-100 text-[12px] uppercase tracking-widest"><Scale size={16} /> Kekayaan Bersih (Harta - Utang)</div>
          <div className="mt-3 font-mono text-4xl font-semibold">{rupiah(d.kekayaan_bersih)}</div>
          <div className="mt-4 flex flex-wrap gap-6 text-sm text-blue-100">
            <span>Total Harta: <b className="text-white font-mono">{rupiah(d.harta_total)}</b></span>
            <span>Total Utang: <b className="text-white font-mono">{rupiah(d.utang_total)}</b></span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Stat label="Total Saldo Akun" value={d.total_saldo_akun} color="text-slate-900" icon={Wallet} testid="card-saldo" />
          <Stat label={`Penghasilan Neto ${tahun}`} value={d.penghasilan_neto} color="text-emerald-700" icon={TrendingUp} testid="card-neto" />
          <Stat label="Total PPh" value={d.total_pph} color="text-amber-700" icon={Receipt} testid="card-pph" />
          <Stat label={d.pph_lebih_bayar > 0 ? "PPh Lebih Bayar (28A)" : "PPh Kurang Bayar (29)"} value={d.pph_lebih_bayar > 0 ? d.pph_lebih_bayar : d.pph_kurang_bayar} color={d.pph_lebih_bayar > 0 ? "text-emerald-700" : "text-rose-700"} icon={TrendingDown} testid="card-kb" />
        </div>

        <TrendKeuanganChart title="Tren Pemasukan vs Pengeluaran" netLabel="Selisih (Tabungan)" />

        <div className="card-flat overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-200 text-sm font-semibold flex items-center gap-2"><Landmark size={16} className="text-slate-500" /> Saldo per Akun</div>
          <table className="w-full text-sm" data-testid="dash-akun-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Akun</th><th className="p-3">Tipe</th><th className="p-3 text-right">Saldo</th>
            </tr></thead>
            <tbody>
              {(d.akun || []).length === 0 && <tr><td colSpan="3" className="text-center py-8 text-slate-400">Belum ada akun. Tambah di menu Kas & Bank.</td></tr>}
              {(d.akun || []).map((a) => (
                <tr key={a.id} className="border-b border-slate-100">
                  <td className="p-3">{a.nama}</td>
                  <td className="p-3 uppercase text-[11px] text-slate-500">{a.tipe}</td>
                  <td className="p-3 text-right font-mono font-semibold">{rupiah(a.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
